import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

import '../contracts/Implantacao_JornadasResponse';
import '../contracts/Implantacao_DetalheResponse';
import '../contracts/Implantacao_ArquivoResponse';
import '../contracts/Implantacao_CriarInput';
import '../contracts/Implantacao_ItemInput';
import '../contracts/Implantacao_AnexoInput';
import '../contracts/Implantacao_ChecklistResponse';
import '../contracts/Implantacao_ChecklistInput';
import '../contracts/Implantacao_ExtraInput';
import '../contracts/Implantacao_ExtraRemoverInput';
import '../contracts/Implantacao_VersaoInput';
import '../contracts/Implantacao_DadosDaFranquiaResponse';
import '../contracts/Implantacao_DadosFranquiaInput';
import '../contracts/Implantacao_DadosBancariosInput';
import '../contracts/Implantacao_EnviarDadoInput';

import type { Implantacao_JornadaRow } from '../contracts/Implantacao_JornadaRow';
import type { Implantacao_EventoRow } from '../contracts/Implantacao_EventoRow';
import type { Implantacao_CriarInput } from '../contracts/Implantacao_CriarInput';
import type { Implantacao_ItemInput } from '../contracts/Implantacao_ItemInput';
import type { Implantacao_ChecklistResponse } from '../contracts/Implantacao_ChecklistResponse';
import type { Implantacao_ChecklistEtapaRow } from '../contracts/Implantacao_ChecklistEtapaRow';
import type { Implantacao_ChecklistItemRow } from '../contracts/Implantacao_ChecklistItemRow';
import type { Implantacao_ExtraInput } from '../contracts/Implantacao_ExtraInput';
import type { Implantacao_DadosFranquiaRow } from '../contracts/Implantacao_DadosFranquiaRow';
import type { Implantacao_DadosBancariosRow } from '../contracts/Implantacao_DadosBancariosRow';
import type { Implantacao_DadosFranquiaInput } from '../contracts/Implantacao_DadosFranquiaInput';
import type { Implantacao_DadosBancariosInput } from '../contracts/Implantacao_DadosBancariosInput';

/**
 * Jornada de implantação do franqueado — da venda à operação liberada (chamado New Shoes
 * 2026-0079, F1). As regras (percentual, etapa atual, pendências, liberação, prazo da COF)
 * vêm prontas do app-api; aqui só se busca, se manda e se traduz o motivo de uma recusa.
 *
 * O checklist é editável em versões (Full) e cada jornada segue a versão em que nasceu, mais os
 * itens extras só dela; as regras dele (itens travados, chaves) também moram no app-api.
 */

export type AcaoDoItem = 'aprovar' | 'recusar' | 'dispensar' | 'reabrir';

const URL_MINHA = '/index.php/implantacao/minha';
const URL_LISTA = '/index.php/implantacao/lista';
const URL_DETALHE = '/index.php/implantacao/detalhe';
const URL_CRIAR = '/index.php/implantacao/criar';
const URL_ITEM = '/index.php/implantacao/item';
const URL_ANEXO = '/index.php/implantacao/anexo';
const URL_ARQUIVO = '/index.php/implantacao/arquivo';
const URL_CHECKLIST = '/index.php/implantacao/checklist';
const URL_CHECKLIST_SALVAR = '/index.php/implantacao/checklist_salvar';
const URL_CHECKLIST_ATUALIZAR = '/index.php/implantacao/checklist_atualizar';
const URL_EXTRA_INCLUIR = '/index.php/implantacao/extra_incluir';
const URL_EXTRA_REMOVER = '/index.php/implantacao/extra_remover';
const URL_ENVIAR_ARQUIVO = '/index.php/implantacao/enviar_arquivo';
const URL_ENVIAR_DADO = '/index.php/implantacao/enviar_dado';
const URL_DADOS = '/index.php/implantacao/dados';
const URL_DADOS_FRANQUIA = '/index.php/implantacao/dados_franquia';
const URL_DADOS_BANCARIOS = '/index.php/implantacao/dados_bancarios';

export type EtapaDoChecklist = Required<Pick<Implantacao_ChecklistEtapaRow, 'chave' | 'titulo'>>;
export type ItemDoChecklist = Required<Pick<Implantacao_ChecklistItemRow, 'chave' | 'etapa' | 'titulo' | 'descricao' | 'tipo' | 'responsavel' | 'obrigatorio' | 'travado'>>;

export const TAMANHO_MAX_ARQUIVO = 10 * 1024 * 1024;

/** O que cada código de recusa do app-api quer dizer para quem está na tela. */
const MOTIVOS: Record<string, string> = {
  data_da_cof_obrigatoria: 'Informe a data em que a COF foi entregue.',
  data_da_cof_no_futuro: 'A data de entrega da COF não pode ser no futuro.',
  cof_sem_data: 'Aprove antes a entrega da COF, com a data.',
  cof_menos_de_10_dias: 'O contrato só pode ser aprovado 10 dias depois da entrega da COF (Lei 13.966/2019).',
  motivo_obrigatorio: 'Escreva o motivo.',
  item_automatico: 'Este item se atualiza sozinho.',
  implantacao_ja_existe: 'Esta franquia já tem um onboarding.',
  implantacao_nao_encontrada: 'Onboarding não encontrado.',
  franquia_nao_encontrada: 'Franquia não encontrada.',
  arquivo_deve_ser_pdf_ou_imagem: 'Envie um PDF ou uma imagem (JPG, PNG ou WebP).',
  arquivo_maior_que_10mb: 'O arquivo passa de 10 MB.',
  arquivo_nao_encontrado: 'Este item não tem arquivo.',
  item_nao_aceita_arquivo: 'Este item não recebe arquivo.',
  checklist_sem_mudanca: 'Nada mudou desde a versão vigente.',
  checklist_sem_etapas: 'O checklist precisa de pelo menos uma etapa.',
  item_travado_removido: 'COF, contrato, taxa de implantação, assinatura e liberação não podem sair do checklist.',
  etapa_sem_itens: 'Toda etapa precisa de pelo menos um item.',
  etapa_sem_titulo: 'Dê um nome a cada etapa.',
  item_sem_titulo: 'Dê um nome a cada item.',
  etapa_com_chave_invalida: 'Há etapas repetidas.',
  item_com_chave_invalida: 'Há itens repetidos.',
  item_em_etapa_inexistente: 'Um item está numa etapa que não existe.',
  tipo_invalido: 'Escolha o tipo do item.',
  responsavel_invalido: 'Escolha de quem é o item.',
  descricao_longa_demais: 'A descrição passa de 500 caracteres.',
  etapas_demais: 'No máximo 12 etapas.',
  itens_demais: 'No máximo 80 itens.',
  extras_demais: 'No máximo 30 itens extras por implantação.',
  extra_inexistente: 'Este item não é extra desta implantação.',
  jornada_ja_na_versao_vigente: 'Este onboarding já está na versão vigente do checklist.',
  cpf_invalido: 'O CPF não é válido.',
  cnpj_invalido: 'O CNPJ não é válido.',
  documento_do_titular_invalido: 'O CNPJ ou CPF do titular não é válido.',
  tipo_de_conta_invalido: 'Escolha corrente ou poupança.',
  data_de_nascimento_invalida: 'A data de nascimento não é válida.',
  uf_invalida: 'A UF tem duas letras (SC, SP…).',
  resposta_obrigatoria: 'Escreva a resposta.',
  item_ja_concluido: 'Este item já foi aprovado pela franqueadora.',
  item_nao_e_do_franqueado: 'Este item é com a franqueadora.',
  item_nao_recebe_arquivo: 'Este item não recebe arquivo.',
  item_nao_recebe_dados: 'Este item recebe um arquivo.',
  implantacao_encerrada: 'Este onboarding já foi encerrado.',
  franquia_fora_do_escopo: 'Esta franquia não é sua.',
  falha_ao_enviar_arquivo: 'Não foi possível guardar o arquivo. Tente de novo.',
};

/** O app-api pode juntar vários códigos numa recusa (problemas do checklist): um por linha. */
export const mensagemDaRecusa = (e: unknown): string => {
  const bruto = e instanceof Error ? e.message : String(e);
  if (bruto.startsWith('faltam_campos:')) return 'Preencha os campos obrigatórios marcados.';
  return bruto.split(' ').every((c) => c in MOTIVOS) ? bruto.split(' ').map((c) => MOTIVOS[c]).join(' ') : (MOTIVOS[bruto] ?? bruto);
};

/** Os campos de um item do checklist, prontos para editar e devolver ao app-api. */
export const paraItemDoChecklist = (i: Implantacao_ChecklistItemRow): ItemDoChecklist => ({
  chave: i.chave ?? '',
  etapa: i.etapa ?? '',
  titulo: i.titulo ?? '',
  descricao: i.descricao ?? '',
  tipo: i.tipo ?? 'tarefa',
  responsavel: i.responsavel ?? 'franqueado',
  obrigatorio: i.obrigatorio ?? true,
  travado: i.travado ?? false,
});

export function useImplantacao() {
  const jornadas = ref<Implantacao_JornadaRow[]>([]);
  const jornada = ref<Implantacao_JornadaRow | null>(null);
  const eventos = ref<Implantacao_EventoRow[]>([]);
  const versaoVigente = ref(1);
  const checklist = ref<NonNullable<Implantacao_ChecklistResponse['dados']> | null>(null);
  const carregando = ref(false);
  const erro = ref<string | null>(null);

  const guardarDetalhe = (dados: { jornada?: Implantacao_JornadaRow[]; eventos?: Implantacao_EventoRow[]; checklist_versao_vigente?: number } | null | undefined): void => {
    jornada.value = dados?.jornada?.[0] ?? null;
    eventos.value = dados?.eventos ?? [];
    versaoVigente.value = dados?.checklist_versao_vigente ?? 1;
  };

  const executar = async <T>(onde: string, fn: () => Promise<T>): Promise<T> => {
    erro.value = null;
    try {
      return await fn();
    } catch (e) {
      logError('useImplantacao', onde, 'Erro', e);
      erro.value = mensagemDaRecusa(e);
      throw e;
    }
  };

  /** As jornadas das franquias de quem está logado (franqueado). */
  const carregarMinhas = async (): Promise<void> => {
    carregando.value = true;
    try {
      await executar('carregarMinhas', async () => {
        const r = await httpService.get(URL_MINHA, { responseContract: 'Implantacao_JornadasResponse' });
        if (!r.success) throw new Error(r.error || 'Erro ao carregar a implantação');
        jornadas.value = r.dados?.jornadas ?? [];
        log('useImplantacao', 'carregarMinhas', 'jornadas', jornadas.value.length);
      });
    } finally {
      carregando.value = false;
    }
  };

  /** Todas as jornadas (franqueadora). */
  const carregarLista = async (): Promise<void> => {
    carregando.value = true;
    try {
      await executar('carregarLista', async () => {
        const r = await httpService.get(URL_LISTA, { responseContract: 'Implantacao_JornadasResponse' });
        if (!r.success) throw new Error(r.error || 'Erro ao carregar as implantações');
        jornadas.value = r.dados?.jornadas ?? [];
      });
    } finally {
      carregando.value = false;
    }
  };

  const carregarDetalhe = async (franquiaId: number): Promise<void> => {
    carregando.value = true;
    try {
      await executar('carregarDetalhe', async () => {
        const r = await httpService.get(`${URL_DETALHE}/${franquiaId}`, { responseContract: 'Implantacao_DetalheResponse' });
        if (!r.success) throw new Error(r.error || 'Erro ao carregar a implantação');
        guardarDetalhe(r.dados);
      });
    } finally {
      carregando.value = false;
    }
  };

  const criar = (dados: Implantacao_CriarInput): Promise<void> =>
    executar('criar', async () => {
      const r = await httpService.post(URL_CRIAR, dados, {
        inputContract: 'Implantacao_CriarInput',
        responseContract: 'Implantacao_DetalheResponse',
      });
      if (!r.success) throw new Error(r.error || 'Erro ao abrir a implantação');
      guardarDetalhe(r.dados);
    });

  const mudarItem = (dados: Implantacao_ItemInput): Promise<void> =>
    executar('mudarItem', async () => {
      const r = await httpService.post(URL_ITEM, dados, {
        inputContract: 'Implantacao_ItemInput',
        responseContract: 'Implantacao_DetalheResponse',
      });
      if (!r.success) throw new Error(r.error || 'Erro ao atualizar o item');
      guardarDetalhe(r.dados);
    });

  const anexar = (franquiaId: number, itemChave: string, arquivo: File): Promise<void> =>
    executar('anexar', async () => {
      if (arquivo.size > TAMANHO_MAX_ARQUIVO) throw new Error('arquivo_maior_que_10mb');
      const formulario = new FormData();
      formulario.append('franquia_id', String(franquiaId));
      formulario.append('item_chave', itemChave);
      formulario.append('arquivo', arquivo);
      // inputContract declarado mesmo sendo multipart: amarra a chamada ao contrato do
      // backend; o httpService pula a validação de forma quando o corpo é FormData.
      const r = await httpService.post(URL_ANEXO, formulario, {
        inputContract: 'Implantacao_AnexoInput',
        responseContract: 'Implantacao_DetalheResponse',
      });
      if (!r.success) throw new Error(r.error || 'Erro ao enviar o arquivo');
      guardarDetalhe(r.dados);
    });

  /** Link temporário (1 h) para o arquivo de um item. */
  const linkDoArquivo = (franquiaId: number, itemChave: string): Promise<string> =>
    executar('linkDoArquivo', async () => {
      const r = await httpService.get(`${URL_ARQUIVO}/${franquiaId}/${itemChave}`, { responseContract: 'Implantacao_ArquivoResponse' });
      if (!r.success || !r.dados?.url) throw new Error(r.error || 'arquivo_nao_encontrado');
      return r.dados.url;
    });

  /** Troca na lista a jornada que o app-api devolveu depois de um envio do franqueado. */
  const trocarJornada = (dados: { jornadas?: Implantacao_JornadaRow[] } | null | undefined): void => {
    const nova = dados?.jornadas?.[0];
    if (!nova) return;
    jornadas.value = jornadas.value.some((j) => j.franquia_id === nova.franquia_id)
      ? jornadas.value.map((j) => (j.franquia_id === nova.franquia_id ? nova : j))
      : [...jornadas.value, nova];
  };

  /** O franqueado manda o arquivo de um item de documento (vai para a conferência). */
  const enviarArquivo = (franquiaId: number, itemChave: string, arquivo: File): Promise<void> =>
    executar('enviarArquivo', async () => {
      if (arquivo.size > TAMANHO_MAX_ARQUIVO) throw new Error('arquivo_maior_que_10mb');
      const formulario = new FormData();
      formulario.append('franquia_id', String(franquiaId));
      formulario.append('item_chave', itemChave);
      formulario.append('arquivo', arquivo);
      const r = await httpService.post(URL_ENVIAR_ARQUIVO, formulario, {
        inputContract: 'Implantacao_AnexoInput',
        responseContract: 'Implantacao_JornadasResponse',
      });
      if (!r.success) throw new Error(r.error || 'Erro ao enviar o arquivo');
      trocarJornada(r.dados);
    });

  /** O franqueado manda um item de dados para a conferência (o formulário já gravado, ou o texto). */
  const enviarDado = (franquiaId: number, itemChave: string, resposta?: string): Promise<void> =>
    executar('enviarDado', async () => {
      const r = await httpService.post(URL_ENVIAR_DADO, { franquia_id: franquiaId, item_chave: itemChave, ...(resposta !== undefined ? { resposta } : {}) }, {
        inputContract: 'Implantacao_EnviarDadoInput',
        responseContract: 'Implantacao_JornadasResponse',
      });
      if (!r.success) throw new Error(r.error || 'Erro ao enviar os dados');
      trocarJornada(r.dados);
    });

  const dadosDaUnidade = ref<Implantacao_DadosFranquiaRow | null>(null);
  const dadosBancarios = ref<Implantacao_DadosBancariosRow | null>(null);
  const guardarDados = (d: { franquia?: Implantacao_DadosFranquiaRow; bancarios?: Implantacao_DadosBancariosRow } | null | undefined): void => {
    dadosDaUnidade.value = d?.franquia ?? null;
    dadosBancarios.value = d?.bancarios ?? null;
  };

  /** Os dados da unidade e os bancários, para o franqueado preencher. */
  const carregarDados = (franquiaId: number): Promise<void> =>
    executar('carregarDados', async () => {
      const r = await httpService.get(`${URL_DADOS}/${franquiaId}`, { responseContract: 'Implantacao_DadosDaFranquiaResponse' });
      if (!r.success) throw new Error(r.error || 'Erro ao carregar os dados');
      guardarDados(r.dados);
    });

  const salvarDadosDaUnidade = (dados: Implantacao_DadosFranquiaInput): Promise<void> =>
    executar('salvarDadosDaUnidade', async () => {
      const r = await httpService.post(URL_DADOS_FRANQUIA, dados, { inputContract: 'Implantacao_DadosFranquiaInput', responseContract: 'Implantacao_DadosDaFranquiaResponse' });
      if (!r.success) throw new Error(r.error || 'Erro ao salvar os dados da unidade');
      guardarDados(r.dados);
    });

  const salvarDadosBancarios = (dados: Implantacao_DadosBancariosInput): Promise<void> =>
    executar('salvarDadosBancarios', async () => {
      const r = await httpService.post(URL_DADOS_BANCARIOS, dados, { inputContract: 'Implantacao_DadosBancariosInput', responseContract: 'Implantacao_DadosDaFranquiaResponse' });
      if (!r.success) throw new Error(r.error || 'Erro ao salvar os dados bancários');
      guardarDados(r.dados);
    });

  /** O checklist vigente e as versões (Full). */
  const carregarChecklist = (): Promise<void> =>
    executar('carregarChecklist', async () => {
      const r = await httpService.get(URL_CHECKLIST, { responseContract: 'Implantacao_ChecklistResponse' });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao carregar o checklist');
      checklist.value = r.dados;
    });

  /** Grava uma versão nova; as jornadas abertas ficam na versão delas. */
  const salvarChecklist = (etapas: EtapaDoChecklist[], itens: ItemDoChecklist[]): Promise<void> =>
    executar('salvarChecklist', async () => {
      const r = await httpService.post(URL_CHECKLIST_SALVAR, { etapas, itens }, {
        inputContract: 'Implantacao_ChecklistInput',
        responseContract: 'Implantacao_ChecklistResponse',
      });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao salvar o checklist');
      checklist.value = r.dados;
    });

  /** Um item só desta implantação. */
  const incluirExtra = (dados: Implantacao_ExtraInput): Promise<void> =>
    executar('incluirExtra', async () => {
      const r = await httpService.post(URL_EXTRA_INCLUIR, dados, {
        inputContract: 'Implantacao_ExtraInput',
        responseContract: 'Implantacao_DetalheResponse',
      });
      if (!r.success) throw new Error(r.error || 'Erro ao incluir o item');
      guardarDetalhe(r.dados);
    });

  const removerExtra = (franquiaId: number, itemChave: string): Promise<void> =>
    executar('removerExtra', async () => {
      const r = await httpService.post(URL_EXTRA_REMOVER, { franquia_id: franquiaId, item_chave: itemChave }, {
        inputContract: 'Implantacao_ExtraRemoverInput',
        responseContract: 'Implantacao_DetalheResponse',
      });
      if (!r.success) throw new Error(r.error || 'Erro ao tirar o item');
      guardarDetalhe(r.dados);
    });

  /** A implantação passa a seguir a versão vigente do checklist. */
  const trazerParaVersaoVigente = (franquiaId: number): Promise<void> =>
    executar('trazerParaVersaoVigente', async () => {
      const r = await httpService.post(URL_CHECKLIST_ATUALIZAR, { franquia_id: franquiaId }, {
        inputContract: 'Implantacao_VersaoInput',
        responseContract: 'Implantacao_DetalheResponse',
      });
      if (!r.success) throw new Error(r.error || 'Erro ao trocar a versão do checklist');
      guardarDetalhe(r.dados);
    });

  return {
    jornadas,
    jornada,
    eventos,
    versaoVigente,
    checklist,
    carregando,
    erro,
    carregarMinhas,
    carregarLista,
    carregarDetalhe,
    criar,
    mudarItem,
    anexar,
    linkDoArquivo,
    carregarChecklist,
    salvarChecklist,
    incluirExtra,
    removerExtra,
    trazerParaVersaoVigente,
    enviarArquivo,
    enviarDado,
    dadosDaUnidade,
    dadosBancarios,
    carregarDados,
    salvarDadosDaUnidade,
    salvarDadosBancarios,
  };
}
